/**
 * SettingsDrawer (Task 53c.5).
 *
 * Quick-access drawer triggered from the Header user area. Mirrors the
 * customer-portal UserMenuDrawer pattern: theme picker + account links
 * + sign out, anchored top-right. Closes on backdrop click and ESC.
 *
 * Doesn't replace the /settings page — deeper flows (password change,
 * MFA, future security options) stay there. The drawer links into them.
 *
 * Accessibility: dialog ARIA, ESC closes, autofocuses the close button
 * on open. No full focus-trap yet (the customer portal's is hook-based;
 * if admin needs it we'll lift the hook into @repro/ui).
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeProvider';
import type { ThemeMode } from '../types/theme';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  userName?: string;
  /** e.g. "Site Admin" / "Org Admin" — shown under the email line. */
  roleLabel?: string;
  onSignOut: () => void;
  signingOut?: boolean;
}

const THEME_OPTS: { value: ThemeMode; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function SettingsDrawer({
  open,
  onClose,
  userEmail,
  userName,
  roleLabel,
  onSignOut,
  signingOut,
}: SettingsDrawerProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const { theme, setTheme } = useTheme();

  // Focus close button on open + handle ESC
  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default bg-base-content/30"
        onClick={onClose}
        aria-label="Close settings menu"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Settings menu"
        className="fixed right-4 top-16 z-50 flex w-80 flex-col gap-4 rounded-box border border-base-300 bg-base-100 p-6 shadow-lg"
      >
        {/* Identity header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5 min-w-0">
            <p className="text-base font-semibold text-base-content truncate" title={userEmail}>
              {userName || userEmail}
            </p>
            {userName && (
              <p className="text-xs text-base-content/60 truncate" title={userEmail}>
                {userEmail}
              </p>
            )}
            {roleLabel && <p className="text-xs text-base-content/60 mt-0.5">{roleLabel}</p>}
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square shrink-0"
            aria-label="Close settings menu"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="h-px w-full bg-base-300" />

        {/* Theme */}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-base-content">Theme</legend>
          <div className="flex gap-2">
            {THEME_OPTS.map((opt) => {
              const active = theme === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  className={`flex-1 rounded-btn border px-3 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'border-primary border-2 bg-base-200 text-base-content'
                      : 'border-base-300 bg-base-200 text-base-content/80 hover:text-base-content'
                  }`}
                  aria-pressed={active}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="h-px w-full bg-base-300" />

        {/* Deeper settings — kept on the /settings route */}
        <nav aria-label="Account settings" className="flex flex-col gap-1">
          <Link
            to="/settings"
            onClick={onClose}
            className="flex items-center gap-2 rounded-btn px-3 py-2 text-sm font-medium text-base-content hover:bg-base-200"
          >
            <Cog6ToothIcon className="h-4 w-4" />
            <span>All settings</span>
          </Link>
          <Link
            to="/settings"
            onClick={onClose}
            className="flex items-center gap-2 rounded-btn px-3 py-2 text-sm font-medium text-base-content hover:bg-base-200"
          >
            <ShieldCheckIcon className="h-4 w-4" />
            <span>Change password</span>
          </Link>
        </nav>

        <div className="h-px w-full bg-base-300" />

        <button
          type="button"
          onClick={onSignOut}
          disabled={signingOut}
          className="btn btn-error btn-sm w-full gap-2"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </>
  );
}
